import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Field,
  FieldContent,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/taskpane/components/ui/field";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Loader, Mic, StopCircle, CornerDownLeft } from "lucide-react";
import SoundWave from "./SoundWave";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import type { GenerateReviewFormData } from "@/schemas/review.schemas";

export function getSelectAllLabel(
  processedOptions: { key: string }[],
  selectedOptionsSet: Set<string>
): string {
  const nonCustomOptions = processedOptions.filter((option) => option.key !== "custom-review");
  const allNonCustomSelected =
    nonCustomOptions.every((option) => selectedOptionsSet.has(option.key)) &&
    nonCustomOptions.length > 0;
  return allNonCustomSelected ? "Deselect All" : "Select All";
}

export function computeOptionChange(
  selectedOptions: string[],
  key: string,
  checked: boolean
): string[] {
  const newSelected = new Set(selectedOptions);
  if (checked) {
    if (key === "custom-review") {
      newSelected.clear();
      newSelected.add(key);
    } else {
      newSelected.delete("custom-review");
      newSelected.add(key);
    }
  } else {
    newSelected.delete(key);
  }
  return Array.from(newSelected);
}

export function computeSelectAll(
  processedOptions: { key: string }[],
  selectedOptionsSet: Set<string>
): string[] {
  const nonCustomOptions = processedOptions.filter((option) => option.key !== "custom-review");
  const allNonCustomSelected = nonCustomOptions.every((option) =>
    selectedOptionsSet.has(option.key)
  );
  let newSelected: Set<string>;
  if (allNonCustomSelected && nonCustomOptions.length > 0) {
    newSelected = new Set();
  } else {
    newSelected = new Set(nonCustomOptions.map((option) => option.key));
  }
  return Array.from(newSelected);
}

export const OptionsCheckboxList: React.FC<{
  processedOptions: { key: string; text: string }[];
  selectedOptionsSet: Set<string>;
  isGenerating: boolean;
  onOptionChange: (key: string, checked: boolean) => void;
}> = ({ processedOptions, selectedOptionsSet, isGenerating, onOptionChange }) => (
  <div className="flex flex-col p-4 gap-2 bg-muted rounded-md border max-h-40 overflow-y-auto">
    {processedOptions.map((option) => (
      <div key={option.key} className="flex items-center gap-2">
        <Checkbox
          className="size-4 border bg-background data-[state=checked]:bg-primary-300 text-neutral-white"
          id={option.key}
          checked={selectedOptionsSet.has(option.key)}
          onCheckedChange={(checked) => onOptionChange(option.key, checked === true)}
          disabled={
            isGenerating ||
            (option.key === "custom-review" &&
              selectedOptionsSet.size > 0 &&
              !selectedOptionsSet.has("custom-review"))
          }
        />
        <FieldLabel htmlFor={option.key} className="text-sm text-neutral-300 font-normal">
          {option.text}
        </FieldLabel>
      </div>
    ))}
  </div>
);

interface DictationState {
  isRecording: boolean;
  isMicroLoading: boolean;
  isConverting: boolean;
  startDictation: () => void;
}

const DictationButton: React.FC<{
  dictation: DictationState;
  isGenerating: boolean;
}> = ({ dictation, isGenerating }) => (
  <Button
    type="button"
    variant={dictation.isRecording ? "destructive" : "outline"}
    size="icon"
    className="size-7 p-0"
    onClick={dictation.startDictation}
    disabled={isGenerating || dictation.isConverting}
    title={dictation.isRecording ? "Stop dictation" : "Dictate"}
  >
    {dictation.isMicroLoading ? (
      <Loader className="size-3.5 animate-spin" />
    ) : dictation.isRecording ? (
      <StopCircle className="size-3.5" />
    ) : (
      <Mic className="size-3.5" />
    )}
  </Button>
);

const ConsiderationTextarea: React.FC<{
  field: Record<string, unknown>;
  isGenerating: boolean;
  dictation: DictationState;
  onKnowledgeBankClick: () => void;
}> = ({ field, isGenerating, dictation, onKnowledgeBankClick }) => (
  <div className="relative">
    <div className="relative">
      {(dictation.isRecording || dictation.isMicroLoading) && (
        <div className="absolute inset-0 z-0 pointer-events-none">
          <SoundWave />
        </div>
      )}
      <Textarea
        className="relative z-10 outline-none resize-none bg-transparent px-4 py-2 pb-9 min-h-[100px] max-h-[120px] text-xs"
        {...field}
        placeholder={
          dictation.isRecording || dictation.isMicroLoading
            ? ""
            : "Enter any specific considerations or requirements"
        }
        disabled={isGenerating}
      />
    </div>
    <div className="absolute right-1.5 bottom-1.5 z-10 flex flex-col gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-7 p-0"
            disabled={isGenerating}
            onClick={onKnowledgeBankClick}
          >
            <CornerDownLeft className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">Insert from knowledge bank</TooltipContent>
      </Tooltip>
      <DictationButton dictation={dictation} isGenerating={isGenerating} />
    </div>
  </div>
);

export const FormInputFields: React.FC<{
  control: ReturnType<typeof useForm<GenerateReviewFormData>>["control"];
  isGenerating: boolean;
  dictation: DictationState;
  onKnowledgeBankClick: () => void;
}> = ({ control, isGenerating, dictation, onKnowledgeBankClick }) => (
  <FieldGroup className="flex flex-col gap-1.5">
    <Controller
      name="representingInput"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid} className="grid gap-1.5">
          <FieldLabel className="text-xs font-medium">Who are you representing?</FieldLabel>
          <FieldContent>
            <Input
              {...field}
              placeholder="Enter client or organisation name"
              disabled={isGenerating}
              className="h-8 text-xs"
            />
          </FieldContent>
          {fieldState.invalid && (
            <FieldError className="text-danger-200 text-xs" errors={[fieldState.error]} />
          )}
        </Field>
      )}
    />

    <Controller
      name="considerationInput"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid} className="grid gap-1.5">
          <FieldLabel className="text-xs font-medium">Something to consider?</FieldLabel>
          <FieldContent>
            <ConsiderationTextarea
              field={field}
              isGenerating={isGenerating}
              dictation={dictation}
              onKnowledgeBankClick={onKnowledgeBankClick}
            />
          </FieldContent>
          {fieldState.invalid && (
            <FieldError className="text-danger-200 text-xs" errors={[fieldState.error]} />
          )}
        </Field>
      )}
    />
  </FieldGroup>
);

export const FormActions: React.FC<{
  hasSelectedOptions: boolean;
  isGenerating: boolean;
  onCancel: () => void;
}> = ({ hasSelectedOptions, isGenerating, onCancel }) => (
  <div className="flex justify-end gap-2 mt-2">
    <Button
      type="button"
      variant={"ghost"}
      size="sm"
      className="border font-medium text-xs"
      onClick={onCancel}
      disabled={isGenerating}
    >
      Cancel
    </Button>
    <Button
      type="submit"
      variant="default"
      size="sm"
      disabled={!hasSelectedOptions || isGenerating}
      className="disabled:bg-primary-200"
    >
      {isGenerating ? (
        <>
          <Loader className="animate-spin" />
          <span className="ml-2">Analysing...</span>
        </>
      ) : (
        "Analyse"
      )}
    </Button>
  </div>
);
